import { useEffect, useState } from 'react';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import SlotImage from '@/components/base/SlotImage';
import { useCollection } from '@/hooks/useCollection';
import { supabase } from '@/lib/supabase';
import type { Testimonial } from '@/lib/types';

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex gap-0.5 text-accent-500" aria-label={`${rating} out of 5 stars`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <i key={i} className={i < rating ? 'ri-star-fill' : 'ri-star-line'} aria-hidden="true" />
      ))}
    </div>
  );
}

export default function TestimonialsPage() {
  const { rows: testimonials, loading, error, reload } = useCollection<Testimonial>('testimonials', 'created_at', false);
  const [name, setName] = useState('');
  const [heading, setHeading] = useState('');
  const [testimonial, setTestimonial] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMsg, setFormMsg] = useState('');
  const MAX_LENGTH = 500;

  useEffect(() => {
    document.title = 'Testimonials — TGMCI Member Stories';
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (honeypot.trim()) { setFormStatus('success'); setFormMsg('Thank you for sharing your story!'); return; }
    if (testimonial.length > MAX_LENGTH) return;
    if (!name || !testimonial) return;
    setFormStatus('loading');

    const { error: insertErr } = await supabase.from('contact_submissions').insert({
      name,
      subject: heading ? `New Testimonial — ${heading}` : 'New Testimonial',
      message: testimonial,
    });

    if (insertErr) {
      setFormStatus('error');
      setFormMsg('Something went wrong. Please try again.');
    } else {
      setFormStatus('success');
      setFormMsg('Thank you for sharing your story with us! It will be reviewed and published soon.');
      setName(''); setHeading(''); setTestimonial('');
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Header />
      <main>
        {/* Hero */}
        <section className="relative pt-40 pb-20 overflow-hidden bg-background-950 text-center">
          <div className="absolute inset-0">
            <SlotImage
              slot="testimonials_hero"
              fallback="https://storage.helloreaddy.io/project_files/e45ec48e-345c-4840-a547-bcc42428da39/740044af-41e0-4bdf-81d7-177bf0e063c3_compressed_WhatsApp-Image-2026-09-12-at-15.29.03-4.webp"
              alt="Church community"
              className="w-full h-full object-cover object-top opacity-15"
            />
          </div>
          <div className="relative z-10 max-w-[700px] mx-auto px-4">
            <p className="text-accent-400 font-label text-sm tracking-widest uppercase font-semibold mb-3">Testimonials</p>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-white leading-tight mb-4">
              What Our Fellow Members Say
            </h1>
            <div className="w-12 h-1 bg-accent-500 mx-auto mb-6" />
            <p className="text-white/60 text-base leading-relaxed">
              Real stories from real people whose lives have been touched and transformed by the TGMCI community.
            </p>
          </div>
        </section>

        {/* Testimonials Grid */}
        <section className="py-20 md:py-28 bg-background-50">
          <div className="max-w-[1200px] mx-auto px-4 md:px-8">
            {loading ? (
              <p className="text-center text-foreground-400 py-16">
                <i className="ri-loader-4-line animate-spin inline-block" aria-hidden="true" /> Loading testimonials...
              </p>
            ) : error ? (
              <div className="text-center py-16">
                <p className="text-red-600 text-sm mb-3">Could not load testimonials.</p>
                <button type="button" onClick={reload} className="btn-primary">Retry</button>
              </div>
            ) : (
              <div className="columns-1 sm:columns-2 lg:columns-3 gap-6">
                {testimonials.map((t) => (
                  <article
                    key={t.id}
                    className="break-inside-avoid mb-6 bg-background-100 rounded-xl p-7 border border-background-200 hover:border-primary-200 transition-all hover:-translate-y-1"
                  >
                    <StarRating rating={t.rating} />
                    <blockquote className="font-heading text-lg italic text-foreground-700 leading-relaxed mt-4 mb-5">
                      &ldquo;{t.text}&rdquo;
                    </blockquote>
                    <div className="flex items-center gap-3 pt-4 border-t border-background-200">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 font-heading font-bold text-sm shrink-0">
                        {t.name.charAt(0)}
                      </div>
                      <div>
                        <div className="font-semibold text-sm text-foreground-800">{t.name}</div>
                        <div className="text-xs text-foreground-400">{t.role}</div>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Share Your Story Form */}
        <section className="py-20 bg-background-100">
          <div className="max-w-[700px] mx-auto px-4 md:px-8">
            <div className="text-center mb-10">
              <p className="text-accent-600 font-label text-sm tracking-widest uppercase font-semibold mb-3">Share Your Story</p>
              <h2 className="font-heading text-3xl font-bold text-foreground-900">Share Your Testimony</h2>
              <div className="w-12 h-1 bg-accent-500 mt-5 mx-auto" />
              <p className="text-foreground-500 mt-5 text-sm">Has TGMCI made a difference in your life? We would love to hear from you!</p>
            </div>

            {formStatus === 'success' ? (
              <div className="text-center p-10 bg-green-50 border border-green-200 rounded-2xl">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-100 text-green-600 mx-auto mb-4">
                  <i className="ri-check-line text-3xl" aria-hidden="true" />
                </div>
                <h3 className="font-heading font-bold text-xl text-green-800 mb-2">Thank You!</h3>
                <p className="text-green-700 text-sm">{formMsg}</p>
                <button type="button" onClick={() => setFormStatus('idle')} className="btn-primary mt-6">
                  Share Another Story
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-background-50 rounded-2xl border border-background-200 p-8 space-y-5">
                <p className="text-xs text-foreground-500">
                  <span className="text-red-500">*</span> Required fields
                </p>

                <div style={{ position: 'absolute', left: '-9999px' }}>
                  <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} />
                </div>

                <div>
                  <label htmlFor="test-name" className="block text-xs font-label font-medium text-foreground-600 uppercase tracking-wide mb-1.5">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="test-name"
                    type="text"
                    name="name"
                    placeholder="What is your full name?"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-4 py-3 border border-background-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
                  />
                </div>

                <div>
                  <label htmlFor="test-heading" className="block text-xs font-label font-medium text-foreground-600 uppercase tracking-wide mb-1.5">
                    Heading (Optional)
                  </label>
                  <input
                    id="test-heading"
                    type="text"
                    name="heading"
                    placeholder="A headline for your testimonial"
                    value={heading}
                    onChange={(e) => setHeading(e.target.value)}
                    className="w-full px-4 py-3 border border-background-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
                  />
                </div>

                <div>
                  <label htmlFor="test-story" className="block text-xs font-label font-medium text-foreground-600 uppercase tracking-wide mb-1.5">
                    Your Testimony <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="test-story"
                    name="testimonial"
                    rows={6}
                    required
                    placeholder="What do you think about us? Share your experience..."
                    value={testimonial}
                    onChange={(e) => setTestimonial(e.target.value.slice(0, MAX_LENGTH))}
                    className="w-full px-4 py-3 border border-background-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 resize-none"
                  />
                  <p className={`text-xs mt-1 text-right ${testimonial.length >= MAX_LENGTH ? 'text-red-500' : 'text-foreground-400'}`}>
                    {testimonial.length}/{MAX_LENGTH}
                  </p>
                </div>

                {formStatus === 'error' && (
                  <p className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg p-3">{formMsg}</p>
                )}

                <button type="submit" disabled={formStatus === 'loading'} className="btn-primary w-full justify-center py-4">
                  {formStatus === 'loading' ? 'Submitting...' : <>Submit Testimonial <i className="ri-arrow-right-line" aria-hidden="true" /></>}
                </button>
              </form>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}